/** What the booking form knows about the voucher code typed into it. */
export type VoucherPreview =
	| { status: "idle" }
	| { status: "loading" }
	| { status: "valid"; discountAmount: number; finalPrice: number }
	| { status: "invalid"; message: string }
	| { status: "error"; message: string };

/** Why the Rails proxy says a therapist (Bonus RO) code was refused for its package. */
export type PreviewReason =
	| "BONUS_RO_PACKAGE_INELIGIBLE"
	| "BONUS_RO_NO_PACKAGE_DISCOUNT";

type PreviewBody = {
	success?: boolean;
	message?: string;
	data?: {
		valid?: boolean;
		message?: string;
		/** set by the Rails proxy; the upstream itself names no reason */
		reason?: PreviewReason;
		discount_amount?: number | null;
		final_price?: number | null;
	};
};

/**
 * Reads the upstream voucher preview (passed through /api/v1/vouchers/preview) into
 * one of the states the form shows. A code the service answered for is valid or
 * invalid; anything else is an error the admin can retry, never a verdict on the code.
 */
export function toVoucherPreview(
	httpStatus: number,
	body: PreviewBody | null,
	fallbackError: string,
	reasonMessages: Record<PreviewReason, string>,
): VoucherPreview {
	const data = body?.data;
	// Error bodies carry raw upstream text (often English, sometimes a stack line);
	// the admin gets the translated retry message instead.
	if (httpStatus >= 400 || !data || typeof data.valid !== "boolean") {
		return { status: "error", message: fallbackError };
	}
	if (!data.valid) {
		if (data.reason && data.reason in reasonMessages) {
			return { status: "invalid", message: reasonMessages[data.reason] };
		}
		return { status: "invalid", message: data.message || fallbackError };
	}
	return {
		status: "valid",
		discountAmount: Number(data.discount_amount ?? 0),
		finalPrice: Number(data.final_price ?? 0),
	};
}
